// A part's own key, for the song that changes key partway through — a chorus that lifts to Em and
// stays there, in a song written in Dm.
//
// `part.key` marks the point of change, not the key of one part. It is declared once, on the part
// where the song moves, and every part after it is in that key until another part declares
// something else. That is how these songs behave: one that goes up rarely comes back down, and
// where it does, the part that brings it back says so itself. Declaring it per part instead would
// mean setting the same key on every part below the change and keeping them in step by hand.
//
// It is stored as an absolute key name rather than as an interval from the song's: an interval
// can't say that a part changed mode as well as pitch (a Dm song with an F major bridge), and it
// isn't how the change is described by the person writing it down, who knows the chorus is in Em.
//
// Two consequences worth knowing:
//
//   Order matters. Because the key carries forward, moving a part moves the point the song changes
//   key, and the parts that used to follow it no longer do. That is the price of declaring it once
//   instead of on every part, and it is the behaviour asked for.
//
//   A duplicate never declares one. Not when it is created, and not by editing it — it takes
//   whatever key is in force where it sits, like any other part. A repeat of a chorus placed after
//   a modulation is in the new key because of where it is, which is the same thing that makes it
//   true of the part beside it.

import i18n from '../i18n';
import { getKeyPitchClass, getKeyRootDistance } from './keyDistance';
import { getKeyAfterTranspose } from './autoTranspose';
import { getKeyScaleNotes } from './chordLibrary';
import { isMinorKey, normalizeKeyDisplay } from './diatonicChords';
import { isSectionPart } from './partNumbers';
import { getSetting, settingKeys } from './settings';
import scales from '../data/scales.json';

const { MAJOR_SCALE_KEYS, MINOR_SCALE_KEYS } = scales;

/**
 * A key spelled the way this reader asked for it, sharps or flats.
 *
 * Every key shown next to another one has to go through this, or two spellings of one key end up
 * side by side and read as two keys.
 *
 * @param {string} key - A key in any spelling the app stores
 * @returns {string} The same key, in the reader's preferred notation
 */
export const spellKey = (key) => normalizeKeyDisplay(key, getSetting(settingKeys.preferSharps) !== false);

/**
 * A chord spelled to match its key as spellKey names it: Gbm in D reads F#m, A# in Bb reads Bb.
 *
 * Only a root or slash bass that is a note of the key's scale is respelled. One outside it — a
 * borrowed Bb in G, any sharp or flat in C — keeps whatever spelling it was written with.
 *
 * @param {string} chord - A chord as written, e.g. 'Gbm7', 'D/Gb', 'A#^1'
 * @param {string} key - The key the chord sits in, in any spelling the app stores
 * @returns {string} The chord for display; unchanged when the key can't be read
 */
export function spellChord(chord, key) {
  if (!chord || getKeyPitchClass(key) === null) return chord;

  const scale = getKeyScaleNotes(spellKey(key));
  const respell = (note) => {
    if (!/[#b]$/.test(note)) return note;

    return scale.find((scaleNote) => getKeyPitchClass(scaleNote) === getKeyPitchClass(note)) ?? note;
  };

  return chord.replace(/^[A-G][#b]?/, respell).replace(/\/([A-G][#b]?)/, (_, bass) => `/${respell(bass)}`);
}

/**
 * Whether two keys name the same key, rather than the same string.
 *
 * The app stores a key in whichever spelling it was written or transposed into — 'A#', 'Bb' and
 * 'A#/Bb' are one key wearing three names — so comparing the strings would report a modulation
 * between a part and a song that are in the same place. Mode counts, though: Dm and D are the same
 * tonic and a genuine change of key.
 *
 * @param {string} a - A key in any spelling the app produces
 * @param {string} b - Another
 * @returns {boolean} False when either key can't be read
 */
export function isSameKey(a, b) {
  const pitchA = getKeyPitchClass(a);
  const pitchB = getKeyPitchClass(b);

  if (pitchA === null || pitchB === null) return false;

  return pitchA === pitchB && isMinorKey(a) === isMinorKey(b);
}

/**
 * A key as it stood before any on-screen transposition — what a stored MIDI clip is anchored
 * against.
 *
 * A transpose rewrites `part.key` along with the chords in it, exactly as it rewrites `song.key`,
 * so the key a part is showing can't be the anchor. Counting back by the offset View has
 * accumulated is exact where re-deriving it from two key names is not.
 *
 * Takes the part's effective key rather than the part, so it needs no say in whether that key was
 * declared here or carried down from an earlier part: a part following the song transposes with
 * the song, and counting back lands on the song's original key either way.
 *
 * @param {string} partKey - The key the part is currently in, from getPartKeys
 * @param {number} transposeSemitones - The offset View has applied, as it counts it
 * @returns {string} The key to measure a clip's shift from
 */
export function getPartOriginalKey(partKey, transposeSemitones = 0) {
  return getKeyAfterTranspose(partKey, -(transposeSemitones || 0));
}

/**
 * The keys a part can be put in, for the picker in the song form.
 *
 * Both modes are offered, which is why `getTransposeKeys` isn't reused: that one narrows the list
 * to the song's own mode, which is right for moving a whole song and wrong here. A part crossing
 * modes is among the commonest changes there is — a song in Dm whose bridge lifts into F, verses
 * in Am against a chorus in C, a last chorus that goes to the parallel major — so a list without
 * them would be missing the case this exists for.
 *
 * The song's own mode is listed first, because a modulation that stays in it is still the likelier
 * one: it puts the few keys anybody actually reaches for at the top, without taking the others
 * away. The groups are returned in that order rather than sorted by the caller, so the picker is
 * handed a list it only has to render.
 *
 * Each key is hinted with its distance from the song's, measured with `getKeyRootDistance` rather
 * than `calculateSemitones`, which can't resolve a pair that crosses modes and answers 0 for it.
 *
 * The "follows the song" option is labelled for the shut control, where it is one of twenty-five
 * things that fit rather than a caption: "Same key" is what it does, and the song's own key moves
 * to `hint`, which the menu shows underneath it.
 *
 * @param {string} songKey - The song's key, which every hint is measured from
 * @returns {{ songOption: { value: string, label: string, hint: string },
 *   groups: Array<{ label: string, options: Array<{ value: string, label: string }> }> }}
 *   The "follows the song" option, carrying the empty value, and the keys in two labelled groups
 */
export function getPartKeyOptions(songKey) {
  const label = (key) => {
    const distance = getKeyRootDistance(songKey, key);

    return distance ? `${spellKey(key)} (${distance > 0 ? '+' : ''}${distance})` : spellKey(key);
  };

  const group = (groupLabel, keys) => ({
    label: groupLabel,
    options: keys.map((key) => ({ value: key, label: label(key) })),
  });

  const major = group(i18n.t('songForm.partKey.major'), MAJOR_SCALE_KEYS);
  const minor = group(i18n.t('songForm.partKey.minor'), MINOR_SCALE_KEYS);

  return {
    songOption: {
      value: '',
      label: i18n.t('songForm.partKey.sameKey'),
      hint: songKey
        ? i18n.t('songForm.partKey.songKey', { key: spellKey(songKey) })
        : i18n.t('songForm.partKey.followsSong'),
    },
    groups: isMinorKey(songKey) ? [minor, major] : [major, minor],
  };
}

/**
 * The key each part is in, and where the song changes key.
 *
 * The one place the carry-forward rule lives. Walks the parts in order holding the key currently in
 * force: a part that declares one moves it, and every part after inherits until something else
 * declares. Shaped like computePartNumbers, and for the same reason — it answers a question about a
 * part that can only be settled by looking at the parts before it, so it is worked out once for the
 * whole list rather than re-derived inside each row.
 *
 * Pass the parts *as rendered* — after the overview and attached-note filters — so a marker lands
 * on what is actually on screen. `from` is set only on a part that moves the key, which is what
 * makes the change show up once, where it happens, instead of on every part that inherits it.
 *
 * A Section divider is the title of the next song in a mix. It holds no chords, so it declares
 * nothing — and it resets the key, because a modulation in the song before it has no bearing on the
 * one after.
 *
 * @param {Array<object>} parts - The parts being rendered, in order
 * @param {string} songKey - The song's key as it currently stands
 * @returns {Object<string, { key: string, from?: string }>} partId to the key it is in, plus the
 *   key it moved from on the parts that move it
 */
export function getPartKeys(parts = [], songKey) {
  const keys = {};
  let inForce = songKey;

  parts.forEach((part) => {
    if (isSectionPart(part)) {
      inForce = songKey;
      keys[part.id] = { key: inForce };

      return;
    }

    // A duplicate takes the key in force where it sits and never declares one of its own
    const declared = part.isDuplicate ? null : part.key;

    if (declared && !isSameKey(declared, inForce)) {
      keys[part.id] = { key: declared, from: inForce };
      inForce = declared;

      return;
    }

    if (declared) inForce = declared;
    keys[part.id] = { key: inForce };
  });

  return keys;
}

/**
 * The key a song is in by the time it finishes — its own, unless a part changed it and nothing
 * changed it back.
 *
 * What the marker between two songs needs. It announces the move a player has to make, and that
 * move starts wherever the song actually left them: a song in Dm whose last chorus lifted to Em
 * hands the band over from Em, so measuring the next song from the stored key would name an
 * interval nobody plays.
 *
 * @param {Array<object>} parts - The parts being rendered, in order
 * @param {string} songKey - The song's key as it currently stands
 * @returns {string} The key in force after the last part, which is songKey when nothing moved it
 */
export function getSongEndKey(parts = [], songKey) {
  const keys = getPartKeys(parts, songKey);
  const lastPart = parts[parts.length - 1];

  return (lastPart && keys[lastPart.id]?.key) || songKey;
}
